import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check } from "lucide-react";
import BonusCapture from "@/components/BonusCapture";

export default function BookDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [owned, setOwned] = useState(false);

  useEffect(() => {
    api.get(`/books/${slug}`).then((r) => setBook(r.data.book));
  }, [slug]);

  useEffect(() => {
    if (!user || !book) return;
    api.get("/library").then((r) => {
      setOwned((r.data.books || []).some((b) => b.id === book.id));
    }).catch(() => {});
  }, [user, book]);

  if (!book) return <div className="min-h-screen flex items-center justify-center"><p className="eyebrow">Loading</p></div>;

  const goBuy = () => {
    navigate(`/checkout/${book.id}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="max-w-[1360px] mx-auto px-6 lg:px-12 pt-16 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="aspect-[3/4] overflow-hidden bg-[#E5E2DC]">
              <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" data-testid="book-cover-image" />
            </div>
            <div className="mt-8 border border-[#E5E2DC] p-6 bg-white">
              <div className="flex items-baseline justify-between">
                <p className="eyebrow">Buy this book</p>
                <p className="font-serif-display text-3xl" data-testid="book-price">${book.price?.toFixed(2)}</p>
              </div>
              {owned ? (
                <Link to="/library" data-testid="already-owned-cta" className="btn-ghost w-full justify-center mt-5">
                  <Check size={16} /> In your library — read
                </Link>
              ) : (
                <button onClick={goBuy} data-testid="buy-book-button" className="btn-primary w-full justify-center mt-5">
                  Purchase & receive PDF
                </button>
              )}
              <ul className="mt-5 space-y-2 text-sm text-[#5C5C5C]">
                <li className="flex gap-2"><Check size={14} className="text-[#2A3B32] mt-1" /> Instant PDF delivered to your email</li>
                <li className="flex gap-2"><Check size={14} className="text-[#2A3B32] mt-1" /> Lifetime access in your Lucid Shelf library</li>
                <li className="flex gap-2"><Check size={14} className="text-[#2A3B32] mt-1" /> No DRM · Read anywhere</li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="eyebrow" data-testid="book-categories">{book.categories?.join(" · ")}</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mt-4" data-testid="book-title">
              {book.title}
            </h1>
            <p className="font-serif-display italic text-2xl sm:text-3xl text-[#5C5C5C] mt-4">{book.subtitle}</p>
            <p className="mt-6 text-sm">{book.pages} pp{book.bonus_pages ? ` + ${book.bonus_pages} pp bonus material` : ""} · {Math.round((book.reading_time_min||0)/60)}h read</p>

            <div className="mt-14 border-l-2 border-[#B85C38] pl-6">
              <p className="font-serif-display text-3xl sm:text-4xl leading-[1.15] italic">
                "{book.sample_excerpt}"
              </p>
            </div>

            {book.sample_chapter && (
              <div className="mt-16 border-t border-[#E5E2DC] pt-12">
                <p className="eyebrow" data-testid="sample-chapter-eyebrow">Read a chapter, on the house</p>
                <h2 className="font-serif-display text-4xl sm:text-5xl mt-3 leading-none tracking-tight">
                  {book.sample_chapter_title || "Chapter 1"}
                </h2>
                <div className="mt-8 max-w-[62ch] space-y-5 text-[17px] leading-[1.75] text-[#1C1C1C]" data-testid="sample-chapter-body">
                  {book.sample_chapter.split(/\n\s*\n/).map((p, i) => (
                    <p key={i} className={i === 0 ? "first-letter:font-serif-display first-letter:text-6xl first-letter:mr-2 first-letter:float-left first-letter:leading-[0.9] first-letter:text-[#B85C38]" : ""}>{p}</p>
                  ))}
                </div>
                <div className="mt-10 border border-[#E5E2DC] p-6 bg-[#F9F8F6] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="eyebrow">Keep reading</p>
                    <p className="font-serif-display text-2xl mt-1">Get the full book + free workbook.</p>
                  </div>
                  <button onClick={goBuy} data-testid="sample-chapter-cta" className="btn-primary flex-shrink-0">Purchase for ${book.price?.toFixed(2)}</button>
                </div>
              </div>
            )}

            <div className="mt-16 border-t border-[#E5E2DC] pt-12">
              <p className="eyebrow">About this book</p>
              <p className="mt-6 text-[17px] leading-[1.7] text-[#1C1C1C]">{book.long_description}</p>
            </div>

            {book.chapters?.length > 0 && (
              <div className="mt-16">
                <p className="eyebrow">Contents</p>
                <ol className="mt-6 divide-y divide-[#E5E2DC] border-t border-b border-[#E5E2DC]">
                  {book.chapters.map((c, i) => (
                    <li key={i} className="py-4 flex justify-between items-baseline" data-testid={`chapter-${i}`}>
                      <span className="font-serif-display text-xl">{c}</span>
                      <span className="eyebrow">Ch. {String(i + 1).padStart(2, "0")}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {book.testimonials?.length > 0 && (
              <div className="mt-16">
                <p className="eyebrow">Notes from readers</p>
                <div className="mt-6 space-y-10">
                  {book.testimonials.map((t, i) => (
                    <blockquote key={i} className="border-l border-[#E5E2DC] pl-6" data-testid={`testimonial-${i}`}>
                      <p className="font-serif-display text-2xl italic leading-snug">"{t.quote}"</p>
                      <p className="mt-3 text-sm text-[#5C5C5C]">— {t.author}, <span className="italic">{t.role}</span></p>
                    </blockquote>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-16">
              <BonusCapture variant="light" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
